﻿import React, { useState, useRef, useEffect } from 'react';
import { FaMicrophone, FaStop, FaPause, FaPlay, FaSave, FaTimes, FaMinus, FaFileAlt, FaCircle } from 'react-icons/fa';
import WaveSurfer from 'wavesurfer.js';
import RecordPlugin from 'wavesurfer.js/dist/plugins/record.js';
import RegionsPlugin from 'wavesurfer.js/dist/plugins/regions.js';
import ZoomPlugin from 'wavesurfer.js/dist/plugins/zoom.js';
import PatientSearchDropdown from './PatientSearchDropdown';
import { apiCall } from '../config/api';
import { useAuth } from '../contexts/AuthContext';

interface AudioRecorderProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (audioBlob: Blob, duration: number, patientId?: string, dictationType?: string) => void;
}

const AudioRecorder: React.FC<AudioRecorderProps> = ({ isOpen, onClose, onSave }) => {
  const { token } = useAuth();
  const [mode, setMode] = useState<'record' | 'upload'>('record');
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isRecorderReady, setIsRecorderReady] = useState(false);
  const [isStartingRecording, setIsStartingRecording] = useState(false);
  const [duration, setDuration] = useState(0);
  const [hasRecording, setHasRecording] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [preservePitch, setPreservePitch] = useState(true);
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<string>('');
  const [isMinimized, setIsMinimized] = useState(false);
  const [inputLevel, setInputLevel] = useState(0);
  const [selectedRegion, setSelectedRegion] = useState<{ start: number; end: number } | null>(null);
  const [isOverwritingRegion, setIsOverwritingRegion] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(30);
  const [waveCurrentTime, setWaveCurrentTime] = useState(0);
  const [waveTotalTime, setWaveTotalTime] = useState(0);
  const [selectedPatient, setSelectedPatient] = useState<string>('');
  const [selectedPatientName, setSelectedPatientName] = useState<string>('');
  const [selectedPatientDob, setSelectedPatientDob] = useState<string>('');
  const [selectedPatientGender, setSelectedPatientGender] = useState<string>('');
  const [dictationType, setDictationType] = useState<string>('');
  const [dictationTypes, setDictationTypes] = useState<string[]>([]);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Refs for WaveSurfer
  const waveformRef = useRef<HTMLDivElement>(null);
  const wavesurferRef = useRef<WaveSurfer | null>(null);
  const recordPluginRef = useRef<RecordPlugin | null>(null);
  const regionsPluginRef = useRef<any>(null);
  const activeRegionRef = useRef<any>(null);
  const selectedDeviceRef = useRef<string>('');
  
  // Audio data
  const recordedBlobRef = useRef<Blob | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const meterDataRef = useRef<Uint8Array<ArrayBuffer> | null>(null);
  const meterAnimationRef = useRef<number | null>(null);
  const micStreamRef = useRef<MediaStream | null>(null);
  const overwriteRegionRef = useRef<{ start: number; end: number } | null>(null);
  const overwriteStopTimerRef = useRef<NodeJS.Timeout | null>(null);

  const speeds = [0.5, 0.75, 1, 1.25, 1.5, 2];

  useEffect(() => {
    selectedDeviceRef.current = selectedDevice;
  }, [selectedDevice]);

  const getThemeTokenValue = (token: string, fallback: string) => {
    if (typeof window === 'undefined') return fallback;
    const value = getComputedStyle(document.documentElement).getPropertyValue(token).trim();
    return value || fallback;
  };

  const resolveThemeColor = (token: string, tokenFallback: string, colorFallback: string) => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return colorFallback;

    const tokenValue = getThemeTokenValue(token, tokenFallback);
    const candidates = [
      `oklch(${tokenValue})`,
      `hsl(${tokenValue})`,
      tokenValue,
      colorFallback,
    ];

    const probe = document.createElement('div');

    for (const candidate of candidates) {
      if (window.CSS?.supports?.('color', candidate)) {
        probe.style.color = candidate;
        break;
      }
    }

    if (!probe.style.color) {
      probe.style.color = colorFallback;
    }

    document.body.appendChild(probe);
    const resolved = getComputedStyle(probe).color || colorFallback;
    probe.remove();

    return resolved;
  };

  const buildWaveGradient = () => {
    if (typeof document === 'undefined') {
      return '#3b82f6';
    }

    const gradientCanvas = document.createElement('canvas');
    gradientCanvas.width = 1;
    gradientCanvas.height = 150;
    const context = gradientCanvas.getContext('2d');

    if (!context) {
      return '#3b82f6';
    }

    const gradient = context.createLinearGradient(0, 0, 0, gradientCanvas.height);
    gradient.addColorStop(0, resolveThemeColor('--p', '210 100% 50%', '#3b82f6'));
    gradient.addColorStop(0.7, resolveThemeColor('--a', '280 90% 55%', '#8b5cf6'));
    gradient.addColorStop(1, resolveThemeColor('--bc', '220 15% 20%', '#111827'));
    return gradient;
  };

  const stopInputLevelMeter = () => {
    if (meterAnimationRef.current !== null) {
      cancelAnimationFrame(meterAnimationRef.current);
      meterAnimationRef.current = null;
    }

    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach((track) => track.stop());
      micStreamRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    analyserRef.current = null;
    meterDataRef.current = null;
    setInputLevel(0);
  };

  const stopOverwriteTimer = () => {
    if (overwriteStopTimerRef.current) {
      clearTimeout(overwriteStopTimerRef.current);
      overwriteStopTimerRef.current = null;
    }
  };

  const startInputLevelMeter = async (deviceId?: string) => {
    stopInputLevelMeter();

    try {
      let stream: MediaStream;

      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: deviceId ? { deviceId: { exact: deviceId } } : true,
        });
      } catch {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      }

      micStreamRef.current = stream;
      const audioContext = new window.AudioContext();
      audioContextRef.current = audioContext;

      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.75;
      source.connect(analyser);

      analyserRef.current = analyser;
      meterDataRef.current = new Uint8Array(new ArrayBuffer(analyser.frequencyBinCount));

      const updateMeter = () => {
        if (!analyserRef.current || !meterDataRef.current) return;

        analyserRef.current.getByteTimeDomainData(meterDataRef.current);
        let sumSquares = 0;

        for (let i = 0; i < meterDataRef.current.length; i += 1) {
          const normalized = (meterDataRef.current[i] - 128) / 128;
          sumSquares += normalized * normalized;
        }

        const rms = Math.sqrt(sumSquares / meterDataRef.current.length);
        const normalizedLevel = Math.min(1, rms * 8);
        setInputLevel(normalizedLevel);
        meterAnimationRef.current = requestAnimationFrame(updateMeter);
      };

      meterAnimationRef.current = requestAnimationFrame(updateMeter);
    } catch (error) {
      console.error('[AudioRecorder] Failed to start input level meter:', error);
      setInputLevel(0);
    }
  };

  const decodeBlobToBuffer = async (audioContext: AudioContext, blob: Blob) => {
    const buffer = await blob.arrayBuffer();
    return audioContext.decodeAudioData(buffer.slice(0));
  };

  const audioBufferToWavBlob = (audioBuffer: AudioBuffer) => {
    const channels = audioBuffer.numberOfChannels;
    const sampleRate = audioBuffer.sampleRate;
    const length = audioBuffer.length;
    const bytesPerSample = 2;
    const blockAlign = channels * bytesPerSample;
    const dataSize = length * blockAlign;
    const buffer = new ArrayBuffer(44 + dataSize);
    const view = new DataView(buffer);

    const writeString = (offset: number, value: string) => {
      for (let i = 0; i < value.length; i += 1) {
        view.setUint8(offset + i, value.charCodeAt(i));
      }
    };

    writeString(0, 'RIFF');
    view.setUint32(4, 36 + dataSize, true);
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, channels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * blockAlign, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, 16, true);
    writeString(36, 'data');
    view.setUint32(40, dataSize, true);

    let offset = 44;
    for (let i = 0; i < length; i += 1) {
      for (let channel = 0; channel < channels; channel += 1) {
        const sample = audioBuffer.getChannelData(channel)[i];
        const clamped = Math.max(-1, Math.min(1, sample));
        view.setInt16(offset, clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff, true);
        offset += 2;
      }
    }

    return new Blob([buffer], { type: 'audio/wav' });
  };

  const overwriteRegionAudio = async (
    originalBlob: Blob,
    replacementBlob: Blob,
    region: { start: number; end: number },
  ) => {
    const audioContext = new AudioContext();

    try {
      const original = await decodeBlobToBuffer(audioContext, originalBlob);
      const replacement = await decodeBlobToBuffer(audioContext, replacementBlob);

      const sampleRate = original.sampleRate;
      const startSample = Math.max(0, Math.floor(region.start * sampleRate));
      const endSample = Math.min(original.length, Math.floor(region.end * sampleRate));
      const targetLength = Math.max(1, endSample - startSample);
      const channels = Math.max(original.numberOfChannels, replacement.numberOfChannels);

      const output = audioContext.createBuffer(channels, original.length, sampleRate);

      for (let channel = 0; channel < channels; channel += 1) {
        const originalChannel = original.getChannelData(Math.min(channel, original.numberOfChannels - 1));
        const replacementChannel = replacement.getChannelData(Math.min(channel, replacement.numberOfChannels - 1));
        const outputChannel = output.getChannelData(channel);

        outputChannel.set(originalChannel.subarray(0, startSample), 0);

        for (let i = 0; i < targetLength; i += 1) {
          outputChannel[startSample + i] = i < replacementChannel.length ? replacementChannel[i] : 0;
        }

        outputChannel.set(originalChannel.subarray(endSample), endSample);
      }

      const blob = audioBufferToWavBlob(output);
      const mergedDuration = output.duration;
      return { blob, mergedDuration };
    } finally {
      await audioContext.close();
    }
  };

  // Load dictation types from API
  useEffect(() => {
    if (isOpen && token) {
      loadDictationTypes();
    }
  }, [isOpen, token]);

  const loadDictationTypes = async () => {
    try {
      console.log('[AudioRecorder] Loading dictation types...');
      const response = await apiCall('/letters/dictation-types/?active_only=true', {
        method: 'GET'
      }, token || undefined);
      
      console.log('[AudioRecorder] Response status:', response.status);
      console.log('[AudioRecorder] Response OK:', response.ok);
      
      if (response.ok) {
        const data = await response.json();
        console.log('[AudioRecorder] Raw API response:', data);
        console.log('[AudioRecorder] data.results:', data.results);
        console.log('[AudioRecorder] Fallback data:', data);
        const types = (data.results || data).map((type: any) => type.name);
        console.log('[AudioRecorder] Extracted types:', types);
        setDictationTypes(types);
      } else {
        const errorText = await response.text();
        console.error('[AudioRecorder] API error response:', errorText);
      }
    } catch (error) {
      console.error('[AudioRecorder] Error loading dictation types:', error);
    }
  };

  useEffect(() => {
    if (isOpen && waveformRef.current) {
      setIsRecorderReady(false);

      // Get available audio devices
      RecordPlugin.getAvailableAudioDevices().then((deviceList) => {
        setDevices(deviceList);
        if (deviceList.length > 0 && !selectedDevice) {
          setSelectedDevice(deviceList[0].deviceId);
        }
      });

      // Main waveform for recorded audio playback
      const ws = WaveSurfer.create({
        container: waveformRef.current,
        waveColor: buildWaveGradient(),
        progressColor: resolveThemeColor('--s', '200 80% 50%', '#0ea5e9'),
        height: 100,
        normalize: true,
        cursorColor: resolveThemeColor('--p', '210 100% 50%', '#3b82f6'),
        cursorWidth: 1,
        minPxPerSec: zoomLevel,
      });

      ws.registerPlugin(ZoomPlugin.create({
        scale: 0.25,
        maxZoom: 400,
      }));

      const regions = ws.registerPlugin(RegionsPlugin.create());
      regions.enableDragSelection({
        color: 'rgba(59, 130, 246, 0.2)',
      });

      regions.on('region-created', (region: any) => {
        regions.getRegions().forEach((existing: any) => {
          if (existing.id !== region.id) {
            existing.remove();
          }
        });
        setSelectedRegion({ start: region.start, end: region.end });
      });

      regions.on('region-updated', (region: any) => {
        setSelectedRegion({ start: region.start, end: region.end });
      });

      regions.on('region-clicked', (region: any, event: MouseEvent) => {
        event.stopPropagation();
        activeRegionRef.current = region;
        region.play(true);
      });

      regions.on('region-in', (region: any) => {
        activeRegionRef.current = region;
      });

      regions.on('region-out', (region: any) => {
        if (activeRegionRef.current === region) {
          activeRegionRef.current = null;
        }
      });

      regions.on('region-removed', () => {
        setSelectedRegion(null);
      });

      // Record plugin
      const record = ws.registerPlugin(RecordPlugin.create({
        continuousWaveform: true,
        continuousWaveformDuration: 300,
        renderRecordedAudio: false,
        mediaRecorderTimeslice: 10,
      }));

      record.on('record-start', () => {
        setIsStartingRecording(false);
        setIsRecording(true);
        setIsPaused(false);
        void startInputLevelMeter(selectedDeviceRef.current || undefined);
      });

      record.on('record-pause', () => {
        setIsPaused(true);
        stopInputLevelMeter();
      });

      record.on('record-resume', () => {
        setIsPaused(false);
        void startInputLevelMeter(selectedDeviceRef.current || undefined);
      });

      record.on('record-progress', (time) => {
        const seconds = Math.floor(time / 1000);
        setDuration(seconds);
        setWaveCurrentTime(seconds);
      });

      record.on('record-end', async (blob: Blob) => {
        setIsStartingRecording(false);
        setIsRecording(false);
        setIsPaused(false);
        stopInputLevelMeter();

        if (isOverwritingRegion && overwriteRegionRef.current && recordedBlobRef.current) {
          try {
            const { blob: mergedBlob, mergedDuration } = await overwriteRegionAudio(
              recordedBlobRef.current,
              blob,
              overwriteRegionRef.current,
            );
            recordedBlobRef.current = mergedBlob;
            setDuration(Math.floor(mergedDuration));
            setHasRecording(true);
            setWaveCurrentTime(0);
            setWaveTotalTime(mergedDuration);
            const mergedUrl = URL.createObjectURL(mergedBlob);
            ws.load(mergedUrl);
          } catch (error) {
            console.error('[AudioRecorder] Failed to overwrite selected region:', error);
          } finally {
            setIsOverwritingRegion(false);
            overwriteRegionRef.current = null;
            stopOverwriteTimer();
          }
          return;
        }

        setHasRecording(true);
        recordedBlobRef.current = blob;
        setWaveCurrentTime(0);
        
        // Load the recorded audio into main wavesurfer for playback
        const url = URL.createObjectURL(blob);
        ws.load(url);
      });

      ws.on('play', () => setIsPlaying(true));
      ws.on('pause', () => setIsPlaying(false));
      ws.on('finish', () => setIsPlaying(false));
      ws.on('timeupdate', (time: number) => {
        setWaveCurrentTime(time);
      });
      ws.on('decode', (decodedDuration: number) => {
        setWaveTotalTime(decodedDuration);
      });
      ws.on('zoom', (minPxPerSec: number) => {
        setZoomLevel(Math.round(minPxPerSec));
      });
      ws.on('interaction', () => {
        activeRegionRef.current = null;
      });

      ws.once('decode', () => {
        ws.zoom(zoomLevel);
      });

      wavesurferRef.current = ws;
      recordPluginRef.current = record;
      regionsPluginRef.current = regions;
      setIsRecorderReady(true);
    }

    return () => {
      stopInputLevelMeter();
      stopOverwriteTimer();
      setIsRecorderReady(false);
      setIsStartingRecording(false);
      if (wavesurferRef.current) {
        wavesurferRef.current.destroy();
      }
      regionsPluginRef.current = null;
      stopTimer();
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      cleanup();
    }
  }, [isOpen]);

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const cleanup = () => {
    stopTimer();
    setIsRecording(false);
    setIsPaused(false);
    setIsRecorderReady(false);
    setIsStartingRecording(false);
    setIsOverwritingRegion(false);
    stopInputLevelMeter();
    stopOverwriteTimer();
    setDuration(0);
    setHasRecording(false);
    setIsPlaying(false);
    setPlaybackRate(1);
    setWaveCurrentTime(0);
    setWaveTotalTime(0);
    recordedBlobRef.current = null;
    
    if (wavesurferRef.current) {
      wavesurferRef.current.destroy();
      wavesurferRef.current = null;
    }
    regionsPluginRef.current = null;
    recordPluginRef.current = null;
    setSelectedRegion(null);
  };

  const startRecording = async () => {
    if (mode !== 'record' || !recordPluginRef.current || !isRecorderReady || isStartingRecording) {
      return;
    }

    try {
      setIsStartingRecording(true);
      const deviceId = selectedDeviceRef.current || undefined;
      await recordPluginRef.current.startRecording({ deviceId });
    } catch (error) {
      setIsStartingRecording(false);
      console.error('[AudioRecorder] Failed to start recording:', error);
      alert('Unable to access microphone. Please check browser microphone permissions and try again.');
    }
  };

  const pauseRecording = async () => {
    if (recordPluginRef.current) {
      try {
        if (isPaused) {
          await recordPluginRef.current.resumeRecording();
        } else {
          await recordPluginRef.current.pauseRecording();
        }
      } catch (error) {
        console.error('[AudioRecorder] Failed to pause/resume recording:', error);
      }
    }
  };

  const stopRecording = async () => {
    if (recordPluginRef.current) {
      try {
        stopOverwriteTimer();
        await recordPluginRef.current.stopRecording();
      } catch (error) {
        console.error('[AudioRecorder] Failed to stop recording:', error);
      }
    }
  };

  const startOverwriteRecording = async () => {
    if (
      !selectedRegion ||
      !recordPluginRef.current ||
      !isRecorderReady ||
      isStartingRecording ||
      isRecording ||
      !recordedBlobRef.current
    ) {
      return;
    }

    try {
      setIsOverwritingRegion(true);
      overwriteRegionRef.current = { ...selectedRegion };
      setIsStartingRecording(true);

      const deviceId = selectedDeviceRef.current || undefined;
      await recordPluginRef.current.startRecording({ deviceId });

      const overwriteDurationMs = Math.max(200, Math.floor((selectedRegion.end - selectedRegion.start) * 1000));
      overwriteStopTimerRef.current = setTimeout(() => {
        void stopRecording();
      }, overwriteDurationMs);
    } catch (error) {
      setIsStartingRecording(false);
      setIsOverwritingRegion(false);
      overwriteRegionRef.current = null;
      stopOverwriteTimer();
      console.error('[AudioRecorder] Failed to start overwrite recording:', error);
    }
  };

  const playRecording = () => {
    if (wavesurferRef.current) {
      wavesurferRef.current.playPause();
    }
  };

  const playSelectedRegion = () => {
    if (!wavesurferRef.current || !selectedRegion) return;
    wavesurferRef.current.play(selectedRegion.start, selectedRegion.end);
  };

  const clearSelectedRegion = () => {
    if (!regionsPluginRef.current) return;
    regionsPluginRef.current.getRegions().forEach((region: any) => region.remove());
    setSelectedRegion(null);
    activeRegionRef.current = null;
  };

  const handleZoomChange = (value: number) => {
    setZoomLevel(value);
    if (wavesurferRef.current) {
      wavesurferRef.current.zoom(value);
    }
  };

  const handlePlaybackRateChange = (rate: number) => {
    setPlaybackRate(rate);
    if (wavesurferRef.current) {
      wavesurferRef.current.setPlaybackRate(rate, preservePitch);
    }
  };

  const handlePreservePitchChange = (preserve: boolean) => {
    setPreservePitch(preserve);
    if (wavesurferRef.current) {
      wavesurferRef.current.setPlaybackRate(playbackRate, preserve);
    }
  };

  const handleSave = () => {
    if (mode === 'record' && recordedBlobRef.current) {
      const patientId = selectedPatient || undefined;
      onSave(recordedBlobRef.current, duration, patientId, dictationType || undefined);
      onClose();
    } else if (mode === 'upload' && uploadedFile) {
      const patientId = selectedPatient || undefined;
      onSave(uploadedFile, duration, patientId, dictationType || undefined);
      onClose();
    }
  };
  
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/m4a', 'audio/mp4', 'audio/webm', 'audio/ogg'];
      if (!validTypes.includes(file.type) && !file.name.match(/\.(mp3|wav|m4a|mp4|webm|ogg)$/i)) {
        alert('Please select a valid audio file (MP3, WAV, M4A, MP4, WebM, or OGG)');
        return;
      }
      
      setUploadedFile(file);
      setHasRecording(true);
      
      // Calculate duration from audio file
      const audio = new Audio();
      const url = URL.createObjectURL(file);
      audio.src = url;
      audio.addEventListener('loadedmetadata', () => {
        setDuration(Math.floor(audio.duration));
        URL.revokeObjectURL(url);
      });
      
      // Load into wavesurfer for playback preview
      if (wavesurferRef.current) {
        wavesurferRef.current.load(url);
      }
    }
  };

  const handleRecordAgain = () => {
    setHasRecording(false);
    setDuration(0);
    recordedBlobRef.current = null;
    if (wavesurferRef.current) {
      wavesurferRef.current.empty();
    }
  };

  const handleMinimize = () => {
    setIsMinimized(true);
  };

  const handleRestore = () => {
    setIsMinimized(false);
  };

  const handleCloseMinimized = () => {
    if (hasRecording || isRecording) {
      const confirmClose = window.confirm('Do you want to save this dictation before closing?');
      if (confirmClose) {
        handleSave();
      } else {
        onClose();
      }
    } else {
      onClose();
    }
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatWaveTime = (seconds: number) => {
    const safeSeconds = Math.max(0, Math.floor(seconds));
    const mins = Math.floor(safeSeconds / 60);
    const secs = safeSeconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!isOpen) return null;

  // Minimized view
  if (isMinimized) {
    return (
      <div className="fixed bottom-4 right-4 z-50 bg-base-100 rounded-lg shadow-2xl border-2 border-primary" style={{ width: '320px' }}>
        <div className="p-4 space-y-3">
          {/* Header */}
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <FaMicrophone className={`text-lg ${isRecording && !isPaused ? 'text-error animate-pulse' : 'text-base-content opacity-60'}`} />
              <span className="font-bold text-sm text-base-content">Dictation</span>
            </div>
            <div className="flex gap-1">
              <button onClick={handleRestore} className="btn btn-ghost btn-xs">
                Restore
              </button>
              <button onClick={handleCloseMinimized} className="btn btn-ghost btn-xs btn-circle">
                <FaTimes />
              </button>
            </div>
          </div>

          {/* Duration */}
          <div className="text-center">
            <div className="text-2xl font-mono font-bold text-base-content">
              {formatDuration(duration)}
            </div>
            <div className="text-xs text-base-content opacity-60">
              {isRecording ? (isPaused ? 'Paused' : 'Recording...') : hasRecording ? 'Ready' : 'Stopped'}
            </div>
          </div>

          {/* Mini controls */}
          <div className="flex justify-center gap-2">
            {isRecording && (
              <>
                <button onClick={pauseRecording} className="btn btn-sm btn-warning gap-1">
                  {isPaused ? <FaPlay /> : <FaPause />}
                </button>
                <button onClick={stopRecording} className="btn btn-sm btn-error gap-1">
                  <FaStop />
                </button>
              </>
            )}

            {hasRecording && !isRecording && (
              <>
                <button onClick={playRecording} className="btn btn-sm btn-info gap-1">
                  {isPlaying ? <FaPause /> : <FaPlay />}
                </button>
                <button onClick={handleSave} className="btn btn-sm btn-success gap-1">
                  <FaSave />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Full view
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-base-100 rounded-lg shadow-xl max-w-6xl w-full">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-base-300">
          <div>
            <h2 className="text-2xl font-bold text-base-content">New Dictation</h2>
            <p className="text-sm text-base-content opacity-70 mt-1">Record or upload a medical dictation</p>
          </div>
          <div className="flex gap-2">
            {!isRecording && (
              <button onClick={handleMinimize} className="btn btn-ghost btn-sm btn-circle">
                <FaMinus />
              </button>
            )}
            <button onClick={onClose} className="btn btn-ghost btn-sm btn-circle">
              <FaTimes />
            </button>
          </div>
        </div>
        
        {/* Tab Switcher */}
        <div className="flex border-b border-base-300">
          <button
            className={`flex-1 py-3 px-4 font-semibold transition-colors ${
              mode === 'record'
                ? 'bg-primary text-primary-content border-b-2 border-primary'
                : 'text-base-content opacity-60 hover:opacity-100'
            }`}
            onClick={() => setMode('record')}
            disabled={isRecording}
          >
            <FaMicrophone className="inline mr-2" />
            Record Audio
          </button>
          <button
            className={`flex-1 py-3 px-4 font-semibold transition-colors ${
              mode === 'upload'
                ? 'bg-primary text-primary-content border-b-2 border-primary'
                : 'text-base-content opacity-60 hover:opacity-100'
            }`}
            onClick={() => setMode('upload')}
            disabled={isRecording}
          >
            <FaFileAlt className="inline mr-2" />
            Upload Audio
          </button>
        </div>

        {/* Body */}
        <div className="p-4 space-y-3">
          {/* Patient and Dictation Type Selection */}
          {!isRecording && !hasRecording && (
            <div className="grid grid-cols-2 gap-3 p-3 bg-base-200 rounded-lg">
              <div className="space-y-1">
                <label className="block text-xs font-medium text-base-content">
                  Patient (Optional)
                </label>
                <PatientSearchDropdown
                  value={selectedPatient}
                  onChange={(patientId, patientName, patientDob, patientGender) => {
                    setSelectedPatient(patientId);
                    setSelectedPatientName(patientName);
                    setSelectedPatientDob(patientDob || '');
                    setSelectedPatientGender(patientGender || '');
                  }}
                  placeholder="Search for a patient..."
                />
                {selectedPatientName && (
                  <div className="text-xs text-base-content opacity-70 mt-1">
                    DOB: {selectedPatientDob || 'Not provided'} | Gender: {selectedPatientGender || 'Not provided'}
                  </div>
                )}
              </div>

              <div className="space-y-1">
                <label className="block text-xs font-medium text-base-content">
                  Dictation Type
                </label>
                <select
                  value={dictationType}
                  onChange={(e) => setDictationType(e.target.value)}
                  className="select select-bordered w-full bg-base-100"
                >
                  <option value="">Select type...</option>
                  {dictationTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
          
          {/* Upload Mode Interface */}
          {mode === 'upload' && !hasRecording && (
            <div className="border-2 border-dashed border-base-300 rounded-lg p-8 text-center">
              <input
                ref={fileInputRef}
                type="file"
                accept="audio/*,.mp3,.wav,.m4a,.mp4,.webm,.ogg"
                onChange={handleFileSelect}
                className="hidden"
              />
              <FaFileAlt className="text-6xl text-base-content opacity-30 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-base-content mb-2">Upload Audio File</h3>
              <p className="text-sm text-base-content opacity-70 mb-4">
                Supported formats: MP3, WAV, M4A, MP4, WebM, OGG
              </p>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="btn btn-primary gap-2"
              >
                <FaFileAlt />
                Choose File
              </button>
            </div>
          )}

          {/* Duration Display */}
          {(mode === 'record' || hasRecording) && (
            <div className="text-center">
              <div className="text-3xl font-mono font-bold text-base-content">
                {formatDuration(duration)}
              </div>
              <div className="text-xs text-base-content opacity-60 mt-1">
                {mode === 'upload' && uploadedFile ? `File: ${uploadedFile.name}` :
                 isRecording ? (isPaused ? 'Paused' : (isOverwritingRegion ? 'Overwriting selection...' : 'Recording...')) : hasRecording ? 'Ready to save' : 'Ready to record'}
              </div>
            </div>
          )}

          {/* Microphone Selection - Record Mode Only */}
          {mode === 'record' && devices.length > 0 && (
            <div className="space-y-1">
              <label className="block text-xs font-medium text-base-content opacity-70">
                Select Microphone
              </label>
              <select
                value={selectedDevice}
                onChange={(e) => setSelectedDevice(e.target.value)}
                className="select select-bordered w-full"
                disabled={isRecording}
              >
                {devices.map((device) => (
                  <option key={device.deviceId} value={device.deviceId}>
                    {device.label || `Microphone ${device.deviceId.slice(0, 8)}`}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Recording Timeline Waveform */}
          {(mode === 'record' || hasRecording) && (
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <label className="block text-xs font-medium text-base-content opacity-70">
                  {isRecording ? 'Recording...' : hasRecording ? 'Recorded Audio' : 'Recording Timeline'}
                </label>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-base-content opacity-60">Mic input</span>
                  <div className="w-5 h-5 rounded-sm border border-base-content/20 bg-base-300 p-0.5">
                    <div
                      className={`w-full rounded-sm transition-all duration-75 ${inputLevel > 0.12 ? 'bg-success' : 'bg-base-content/40'}`}
                      style={{
                        height: `${Math.max(2, Math.round(inputLevel * 100))}%`,
                        marginTop: `${100 - Math.max(2, Math.round(inputLevel * 100))}%`,
                      }}
                    />
                  </div>
                </div>
              </div>
              <div className="bg-neutral rounded p-1 relative overflow-hidden z-0">
                <div 
                  ref={waveformRef}
                  style={{ height: '100px' }}
                />
              </div>
              <div className="flex items-center justify-between text-xs text-base-content/70 mt-1">
                <span>
                  Time: {formatWaveTime(waveCurrentTime)} / {formatWaveTime(waveTotalTime || duration)}
                </span>
                <span>
                  Zoom: {Math.round(zoomLevel)} px/s
                </span>
              </div>
            </div>
          )}

          {/* Playback Controls */}
          {hasRecording && !isRecording && (
            <div className="space-y-2 p-3 bg-base-200 rounded-lg">
              {selectedRegion && (
                <div className="flex items-center justify-between text-xs text-base-content/80">
                  <span>
                    Selection: {selectedRegion.start.toFixed(1)}s - {selectedRegion.end.toFixed(1)}s
                  </span>
                  <button onClick={clearSelectedRegion} className="btn btn-ghost btn-xs">
                    Clear selection
                  </button>
                </div>
              )}
              <div className="flex items-center gap-3">
                <label className="text-xs font-medium text-base-content whitespace-nowrap">
                  Playback Speed: {playbackRate.toFixed(2)}x
                </label>
                <input
                  type="range"
                  min="0"
                  max={speeds.length - 1}
                  step="1"
                  value={speeds.indexOf(playbackRate)}
                  onChange={(e) => handlePlaybackRateChange(speeds[Number(e.target.value)])}
                  className="range range-primary flex-1"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="preservePitch"
                  checked={preservePitch}
                  onChange={(e) => handlePreservePitchChange(e.target.checked)}
                  className="checkbox checkbox-sm"
                />
                <label htmlFor="preservePitch" className="text-sm text-base-content cursor-pointer">
                  Preserve pitch
                </label>
              </div>
              <div className="flex items-center gap-2 flex-1">
                <label htmlFor="waveZoom" className="text-sm text-base-content whitespace-nowrap">
                  Zoom
                </label>
                <input
                  id="waveZoom"
                  type="range"
                  min="10"
                  max="400"
                  step="10"
                  value={zoomLevel}
                  onChange={(e) => handleZoomChange(Number(e.target.value))}
                  className="range range-primary range-sm"
                />
              </div>
            </div>
          )}

          {/* Control Buttons */}
          <div className="flex justify-center gap-3 pt-2 relative z-20 flex-wrap">
            {mode === 'record' && !isRecording && !hasRecording && (
              <button
                onClick={startRecording}
                className="btn btn-primary btn-lg gap-2"
                disabled={!isRecorderReady || isStartingRecording}
              >
                <FaCircle className="text-xl text-error" />
                {!isRecorderReady ? 'Initializing...' : isStartingRecording ? 'Starting...' : 'Start Recording'}
              </button>
            )}

            {mode === 'record' && isRecording && (
              <>
                <button
                  onClick={pauseRecording}
                  className="btn btn-warning btn-lg gap-2"
                  disabled={!isRecorderReady || isStartingRecording}
                >
                  {isPaused ? <FaPlay className="text-xl" /> : <FaPause className="text-xl" />}
                  {isPaused ? 'Resume' : 'Pause'}
                </button>

                <button
                  onClick={stopRecording}
                  className="btn btn-error btn-lg gap-2"
                  disabled={!isRecorderReady || isStartingRecording}
                >
                  <FaStop className="text-xl" />
                  Stop
                </button>
              </>
            )}

            {hasRecording && !isRecording && (
              <>
                <button
                  onClick={playRecording}
                  className={`btn btn-lg gap-2 ${isPlaying ? 'btn-warning' : 'btn-info'}`}
                >
                  <FaPlay className="text-xl text-success" />
                  {isPlaying ? 'Pause' : 'Play'}
                </button>

                {selectedRegion && (
                  <button
                    onClick={playSelectedRegion}
                    className="btn btn-outline btn-lg gap-2"
                  >
                    <FaPlay className="text-xl text-success" />
                    Play Selection
                  </button>
                )}

                {mode === 'record' && selectedRegion && (
                  <button
                    onClick={startOverwriteRecording}
                    className="btn btn-warning btn-lg gap-2"
                    disabled={!isRecorderReady || isStartingRecording}
                  >
                    <FaCircle className="text-xl text-error" />
                    Re-record Selection
                  </button>
                )}

                {mode === 'record' && (
                  <button
                    onClick={handleRecordAgain}
                    className="btn btn-ghost btn-lg gap-2"
                  >
                    <FaMicrophone className="text-xl" />
                    Record Again
                  </button>
                )}

                <button
                  onClick={handleSave}
                  className="btn btn-success btn-lg gap-2"
                >
                  <FaSave className="text-xl" />
                  Save Dictation
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AudioRecorder;
